/**
 * `/login/totp` — the second factor after password (routing-and-state.md §1). Submits
 * the 6-digit code; the store re-POSTs `/auth/login` with the held credentials +
 * `totp_code`. On success → the firm picker (/firms), or back to the invitation the sign-in
 * started from (`?redirect=/invite/<token>`, carried from /login). If the user reached this page
 * without a pending sign-in (e.g. a refresh dropped the in-memory credentials), it bounces back
 * to /login. A recovery-code link covers a lost authenticator — a router link, so the in-memory
 * sign-in state is not thrown away by a full reload on the way there.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type JSX } from 'react';

import { AuthCard, FormError, SubmitButton, TextField } from '../components/ui.tsx';
import { getErrorMessage } from '../lib/api.ts';
import { carriedRedirect, useAfterSignIn } from '../router/redirect.ts';
import { useAuth } from '../store/auth-store.ts';

export function TotpScreen(): JSX.Element {
  const navigate = useNavigate();
  const afterSignIn = useAfterSignIn();
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  const carry = carriedRedirect(redirect);
  const submitTotp = useAuth((s) => s.submitTotp);
  const hasPending = useAuth((s) => s._pendingCredentials !== null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!hasPending) void navigate({ to: '/login', search: carriedRedirect(redirect) });
  }, [hasPending, navigate, redirect]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await submitTotp(code.trim());
      if (result.outcome === 'authenticated') {
        // Identity signed in → the firm picker (single-membership identities auto-select
        // there), or back to the invitation being accepted.
        await afterSignIn(redirect);
      } else if (result.outcome === 'totp_enrolment_required') {
        await navigate({ to: '/login/totp-enrol', search: carry });
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Two-factor authentication"
      subtitle="Enter the 6-digit code from your authenticator app"
    >
      <form
        onSubmit={handleSubmit}
        aria-label="Enter authenticator code"
        style={{ display: 'grid', gap: 12 }}
      >
        <TextField
          label="Authentication code"
          name="totp_code"
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
        />
        <FormError message={error} />
        <SubmitButton disabled={busy || code.length !== 6}>
          {busy ? 'Verifying…' : 'Verify'}
        </SubmitButton>
        <Link to="/login/recovery" search={carry} style={{ fontSize: 13 }}>
          Lost your device? Use a recovery code
        </Link>
      </form>
    </AuthCard>
  );
}
